import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { formatMoney, formatQuality } from '@/lib/format';
import type { PurchasePriceList } from '@/types/buy';
import {
  EMPTY_ROW,
  PRICE_COLUMNS,
  PRICE_MAX,
  PRICE_MIN,
  isOutOfRange,
  priceCellId,
  rowKey,
  type PriceColumn,
  type PriceDraft,
  type PriceDraftRow,
} from './priceListForm';
import classes from './PriceRows.module.css';

interface PriceRowsProps {
  readonly list: PurchasePriceList;
  readonly draft: PriceDraft;
  /** Стабильный обработчик: строка перерисовывается, только когда меняется её собственная цена. */
  readonly onChange: (key: string, column: PriceColumn, value: number | string) => void;
  /** Была попытка отправки: цена ниже минимума отмечается и без ухода с поля. */
  readonly attempted: boolean;
}

// Цены по строкам качества. На компьютере — одна таблица с общими колонками, классы культуры подзаголовками; на
// телефоне — название качества и под ним два поля рядом. Раскладка выбирается по ширине экрана, а не скрывается
// стилями: иначе обе раскладки строились бы одновременно и каждое поле существовало бы дважды.
export default function PriceRows({ list, draft, onChange, attempted }: PriceRowsProps) {
  const { t } = useTranslation();
  // Ширина читается сразу, а не после первого кадра: иначе на телефоне сначала мелькнула бы таблица.
  const wide = useMediaQuery('(min-width: 48em)', true, { getInitialValueInEffect: false });
  const withClassTitles = list.goodsList.length > 1 || list.goodsList.some((item) => item.goodsType !== item.parentGoodsType);
  const label = (column: PriceColumn): string => t(column === 'p0' ? 'app.buy.prepayment0Short' : 'app.buy.prepayment100Short');

  if (!wide) {
    return (
      <div className={classes.cards}>
        {list.goodsList.map((item) => (
          <div key={item.goodsType}>
            {withClassTitles ? <h3 className={classes.classTitle}>{item.goodsType}</h3> : null}
            {item.qualityRows.map((row) => {
              const key = rowKey(item.goodsType, row.propsSetId);
              const quality = formatQuality(row.properties) || item.goodsType;
              return (
                <div key={row.propsSetId} className={classes.card}>
                  <p className={classes.cardQuality}>{quality}</p>
                  <div className={classes.cardFields}>
                    <PriceCells rowKeyValue={key} quality={quality} row={draft[key] ?? EMPTY_ROW} onChange={onChange} attempted={attempted} visibleLabels />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <table className={classes.table}>
      <colgroup>
        <col className={classes.colQuality} />
        <col />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">{t('app.buy.quality')}</th>
          {PRICE_COLUMNS.map((column) => (
            <th key={column} scope="col">
              {label(column)}
            </th>
          ))}
        </tr>
      </thead>
      {list.goodsList.map((item) => (
        <tbody key={item.goodsType}>
          {withClassTitles ? (
            <tr className={classes.classRow}>
              <th scope="rowgroup" colSpan={3}>
                {item.goodsType}
              </th>
            </tr>
          ) : null}
          {item.qualityRows.map((row) => {
            const key = rowKey(item.goodsType, row.propsSetId);
            const quality = formatQuality(row.properties) || item.goodsType;
            return (
              <tr key={row.propsSetId}>
                <th scope="row" className={classes.quality}>
                  {quality}
                </th>
                <PriceCells rowKeyValue={key} quality={quality} row={draft[key] ?? EMPTY_ROW} onChange={onChange} attempted={attempted} cellTag="td" />
              </tr>
            );
          })}
        </tbody>
      ))}
    </table>
  );
}

interface PriceCellsProps {
  readonly rowKeyValue: string;
  readonly quality: string;
  readonly row: PriceDraftRow;
  readonly onChange: PriceRowsProps['onChange'];
  readonly attempted: boolean;
  /** Подпись над полем (телефон); на компьютере подпись — в шапке таблицы и в `aria-label`. */
  readonly visibleLabels?: boolean;
  readonly cellTag?: 'td';
}

// Пара полей одной строки качества. Мемоизирована: ввод в одно поле не перерисовывает остальные строки.
function PriceCellsView({ rowKeyValue, quality, row, onChange, attempted, visibleLabels, cellTag }: PriceCellsProps) {
  const { t } = useTranslation();
  // Поля, с которых уже уходил фокус. Цена ниже минимума в процессе набора («1», «15», «150») — не ошибка: её
  // отмечаем после ухода с поля или попытки отправки. Цену выше максимума — сразу.
  const [left, setLeft] = useState<ReadonlySet<PriceColumn>>(() => new Set());
  const showError = (column: PriceColumn): boolean => {
    const value = row[column];
    return isOutOfRange(value) && (value !== '' && value > PRICE_MAX ? true : attempted || left.has(column));
  };
  const labels = (column: PriceColumn): string => t(column === 'p0' ? 'app.buy.prepayment0Short' : 'app.buy.prepayment100Short');
  return (
    <>
      {PRICE_COLUMNS.map((column) => {
        const input = (
          <NumberInput
            id={priceCellId(rowKeyValue, column)}
            label={visibleLabels ? labels(column) : undefined}
            aria-label={visibleLabels ? undefined : `${quality}, ${labels(column)}`}
            value={row[column]}
            onChange={(value) => onChange(rowKeyValue, column, value)}
            onBlur={() => setLeft((current) => (current.has(column) ? current : new Set(current).add(column)))}
            error={showError(column) ? t('app.buy.form.priceErr', { min: formatMoney(PRICE_MIN), max: formatMoney(PRICE_MAX) }) : undefined}
            allowNegative={false}
            allowDecimal={false}
            thousandSeparator=" "
            clampBehavior="none"
            placeholder="—"
            hideControls
            inputMode="numeric"
          />
        );
        return cellTag ? <td key={column}>{input}</td> : <div key={column}>{input}</div>;
      })}
    </>
  );
}

const PriceCells = memo(PriceCellsView);
